import { Box, Chip, LinearProgress, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { format, isValid, parseISO } from 'date-fns';
import { isValidElement } from 'react';

import { DataFileType } from 'common/src/types/enums/DataFileType.enum';

export type ValueDisplayType =
  | string
  | number
  | Date
  | JSX.Element
  | null
  | undefined;

export const DataFileTypeChip = ({
  label,
  size = 'medium',
}: {
  label: DataFileType;
  size?: 'small' | 'medium';
}) => (
  <Chip
    label={label}
    color={label === DataFileType.Emailed ? 'info' : 'primary'}
    variant={label === DataFileType.Uploaded ? 'outlined' : 'filled'}
    size={size}
  />
);

const ValueDisplay = ({
  direction,
  value,
}: {
  direction: 'row' | 'column';
  value: ValueDisplayType;
}) => {
  let displayValue = value || '-';

  if (isValidElement(displayValue)) {
    return displayValue;
  }

  if (value instanceof Date) {
    const dateValue = typeof value === 'string' ? parseISO(value) : value;

    displayValue = isValid(dateValue) ? format(dateValue, 'Pp') : '-';
  }

  return (
    <Typography variant="subtitle2" sx={{ ml: direction === 'row' ? 0 : 1 }}>
      {displayValue as string}
    </Typography>
  );
};

export const DataDisplay = ({
  label,
  value,
  direction = 'row',
}: {
  label: string;
  value: ValueDisplayType;
  direction?: 'row' | 'column';
}) => {
  const isRow = direction === 'row';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: direction,
        alignItems: isRow ? 'baseline' : 'flex-start',
        gap: isRow ? 1 : 0,
        flexWrap: isRow ? 'wrap' : 'nowrap',
      }}
    >
      <Typography variant="subtitle1" color="text.secondary">
        {label}:
      </Typography>
      <ValueDisplay direction={direction} value={value} />
    </Box>
  );
};

export const QualityScoreDisplay = ({ score }: { score: number }) => {
  const newScore = score || 0;
  const scorePercentage = (newScore / 5) * 100;

  return (
    <Box display="flex" alignItems="center" gap={1} pt={0.4} width={130}>
      <LinearProgress
        variant="determinate"
        value={scorePercentage}
        color="secondary"
        sx={{
          height: 10,
          width: 100,
          borderRadius: 5,
          backgroundColor: grey[300],
        }}
      />
      <Typography variant="caption">{newScore}/5</Typography>
    </Box>
  );
};
